/**
 * Attestation payload compatibility for mixed host builds.
 *
 * Current native code signs `{ clerkUserId, machineId, nonce }` only.
 * Older PlaycastService builds still signed with an extra `timestamp` field.
 * After #14485 the renderer stopped including `timestamp` in the POST body,
 * which breaks attestation against those old natives (`INVALID_ATTESTATION_SIGNATURE`).
 *
 * Freshness/replay is the server nonce only — client wall-clock is never trusted.
 * `timestamp` is replayed solely so the verified bytes match what the old TPM signed.
 */

export type AttestationPayloadFields = {
  machineId: string;
  clerkUserId: string;
  nonce: string;
  /** Legacy signed field only; not used for freshness. */
  timestamp?: number;
};

export type AttestationCompatInput = {
  machineId: string;
  aikPublicKey: string;
  seedSignature: string;
  attestationSignature: string;
  attestationPayload: AttestationPayloadFields;
  /**
   * Wall-clock ms echoed by C++ in the attestation reply. Not trusted for
   * freshness. Used only to reconstruct the legacy four-field signed payload.
   */
  replyTimestamp?: number;
};

export type LocalAttestationVerifyResult = {
  seedValid: boolean;
  payloadValid: boolean;
  reason?: string;
};

/**
 * Canonicalize exactly as realtime-api `canonicalJson`: recursively sort keys,
 * no whitespace. Exported for unit tests of the shared contract.
 */
export function canonicalizeAttestationPayload(value: unknown): string {
  const sortKeys = (input: unknown): unknown => {
    if (Array.isArray(input)) {
      return input.map(sortKeys);
    }
    if (input !== null && typeof input === 'object') {
      return Object.keys(input as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = sortKeys((input as Record<string, unknown>)[key]);
          return acc;
        }, {});
    }
    return input;
  };

  return JSON.stringify(sortKeys(value));
}

/**
 * Pure selection: prefer the modern three-field payload; fall back to the
 * legacy timestamp-bearing payload only when local verify says it matches.
 */
export function chooseAttestationPayloadVariant(options: {
  threeFieldPayloadValid: boolean;
  legacyFourFieldPayloadValid: boolean;
  hasLegacyTimestamp: boolean;
}): 'three_field' | 'legacy_four_field' {
  if (options.threeFieldPayloadValid) {
    return 'three_field';
  }
  if (options.hasLegacyTimestamp && options.legacyFourFieldPayloadValid) {
    return 'legacy_four_field';
  }
  return 'three_field';
}

/**
 * Verify seed + attestation signatures locally with Web Crypto using the same
 * algorithm/format as realtime-api (ECDSA P-256, SHA-256, IEEE P1363, BCRYPT
 * ECC public blob).
 */
export async function verifyAttestationLocally(
  attestationData: AttestationCompatInput,
): Promise<LocalAttestationVerifyResult> {
  try {
    // Explicit Uint8Array<ArrayBuffer> typing: TS 5.7+ defaults Uint8Array to
    // <ArrayBufferLike>, which SubtleCrypto's BufferSource param rejects.
    const b64ToBytes = (b64: string): Uint8Array<ArrayBuffer> => {
      const bin = atob(b64);
      const buf = new ArrayBuffer(bin.length);
      const out = new Uint8Array(buf);
      for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
      return out;
    };
    const bytesToB64Url = (bytes: Uint8Array<ArrayBuffer>): string =>
      btoa(String.fromCharCode(...bytes))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');

    const pubKeyBuf = b64ToBytes(attestationData.aikPublicKey);
    if (pubKeyBuf.length < 8) {
      return { seedValid: false, payloadValid: false, reason: 'aikPublicKey too short' };
    }
    const dv = new DataView(pubKeyBuf.buffer, pubKeyBuf.byteOffset, pubKeyBuf.byteLength);
    const magic = dv.getUint32(0, true);
    const cbKey = dv.getUint32(4, true);
    if (magic !== 0x31534345 || cbKey !== 32 || pubKeyBuf.length < 8 + cbKey * 2) {
      return {
        seedValid: false,
        payloadValid: false,
        reason: `bad BCRYPT_ECCPUBLIC_BLOB header magic=0x${magic.toString(16)} cbKey=${cbKey} len=${pubKeyBuf.length}`,
      };
    }

    const x = pubKeyBuf.slice(8, 8 + cbKey);
    const y = pubKeyBuf.slice(8 + cbKey, 8 + cbKey * 2);

    const cryptoKey = await crypto.subtle.importKey(
      'jwk',
      { kty: 'EC', crv: 'P-256', x: bytesToB64Url(x), y: bytesToB64Url(y) },
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify'],
    );

    const seedSigBytes = b64ToBytes(attestationData.seedSignature);
    const seedDataBytes = b64ToBytes(attestationData.machineId);
    const seedValid = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      cryptoKey,
      seedSigBytes,
      seedDataBytes,
    );

    const canonicalPayload = canonicalizeAttestationPayload(attestationData.attestationPayload);
    const payloadSigBytes = b64ToBytes(attestationData.attestationSignature);
    const payloadDataBytes = new TextEncoder().encode(canonicalPayload);
    const payloadValid = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      cryptoKey,
      payloadSigBytes,
      payloadDataBytes,
    );

    const payloadFieldNames = Object.keys(attestationData.attestationPayload).sort();
    console.log(
      '[ATTESTATION_DIAGNOSTIC] canonical payload fields:',
      payloadFieldNames,
      'encoded bytes:',
      payloadDataBytes.length,
    );
    console.log(
      '[ATTESTATION_DIAGNOSTIC] seed sig length (bytes):',
      seedSigBytes.length,
      '(expect 64 for ECDSA P-256 IEEE P1363)',
    );
    console.log(
      '[ATTESTATION_DIAGNOSTIC] payload sig length (bytes):',
      payloadSigBytes.length,
      '(expect 64 for ECDSA P-256 IEEE P1363)',
    );

    return { seedValid, payloadValid };
  } catch (e) {
    return {
      seedValid: false,
      payloadValid: false,
      reason: `local verify threw: ${(e as Error)?.message ?? String(e)}`,
    };
  }
}

/**
 * Prefer the modern three-field payload. If local verify fails and C++ echoed
 * a reply timestamp, retry with the legacy four-field payload so old natives
 * that still signed `timestamp` can attest without burning a second nonce.
 */
export async function resolveCompatibleAttestationData<T extends AttestationCompatInput>(
  attestationData: T,
): Promise<T> {
  const threeFieldPayload: AttestationPayloadFields = {
    machineId: attestationData.attestationPayload.machineId,
    clerkUserId: attestationData.attestationPayload.clerkUserId,
    nonce: attestationData.attestationPayload.nonce,
  };
  const threeFieldData = { ...attestationData, attestationPayload: threeFieldPayload };
  const threeFieldVerify = await verifyAttestationLocally(threeFieldData);

  const replyTimestamp = attestationData.replyTimestamp;
  const hasLegacyTimestamp =
    typeof replyTimestamp === 'number' && Number.isFinite(replyTimestamp) && replyTimestamp > 0;

  let legacyFourFieldValid = false;
  let legacyData: T | undefined;

  if (!threeFieldVerify.payloadValid && hasLegacyTimestamp) {
    legacyData = {
      ...attestationData,
      attestationPayload: {
        ...threeFieldPayload,
        timestamp: replyTimestamp,
      },
    };
    const legacyVerify = await verifyAttestationLocally(legacyData);
    legacyFourFieldValid = legacyVerify.payloadValid;
    console.log('[ATTESTATION_COMPAT] three-field local verify failed; legacy four-field:', {
      payloadValid: legacyFourFieldValid,
      seedValid: legacyVerify.seedValid,
      reason: legacyVerify.reason,
    });
  }

  const variant = chooseAttestationPayloadVariant({
    threeFieldPayloadValid: threeFieldVerify.payloadValid,
    legacyFourFieldPayloadValid: legacyFourFieldValid,
    hasLegacyTimestamp,
  });

  if (variant === 'legacy_four_field' && legacyData) {
    console.log(
      '[ATTESTATION_COMPAT] Using legacy timestamp-bearing payload for old native build compatibility',
    );
    return legacyData;
  }

  if (!threeFieldVerify.payloadValid) {
    console.warn('[ATTESTATION_COMPAT] Submitting three-field payload after local verify failure', {
      seedValid: threeFieldVerify.seedValid,
      reason: threeFieldVerify.reason,
      triedLegacy: hasLegacyTimestamp,
      legacyFourFieldValid,
    });
  }

  return threeFieldData;
}
